/**
 * ReviewCompletionHandoff — Milestone 37.3.
 *
 * The final moment of a Review Session. Displays the completion
 * summary and recommends exactly one meaningful next mission per the
 * completion matrix in the spec (§8.2).
 *
 * Never a dead-end. Every completion ends with the founder pointed at
 * the next best step.
 */
import { CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

export type CompletionHandoffKind =
  | "vault_registration"   // some approved awaiting Vault
  | "ready_to_schedule"    // all approved + vaulted → next step: Publishing Calendar
  | "already_scheduling"   // some scheduled → next step: Publishing Calendar
  | "more_items_pending"   // drift: new items appeared → new session
  | "back_to_campaign"     // fallback / no-work state
  | "no_work_needed";      // queue empty on entry

const COPY: Record<CompletionHandoffKind, {
  title: string;
  detail: string;
  primaryLabel: string;
  primaryRoute: string;
  secondaryLabel: string;
  secondaryRoute: string;
}> = {
  vault_registration: {
    title: "Great work.",
    detail: "Approved assets are moving into the Vault.",
    primaryLabel: "Open Vault →",
    primaryRoute: "/vault",
    secondaryLabel: "Back to Executive Home",
    secondaryRoute: "/home",
  },
  ready_to_schedule: {
    title: "All approved.",
    detail: "Every asset is in the Vault. Time to schedule.",
    primaryLabel: "Open Publishing Calendar →",
    primaryRoute: "/publishing/calendar",
    secondaryLabel: "Back to Campaign Room",
    secondaryRoute: "/campaigns",
  },
  already_scheduling: {
    title: "Scheduling is underway.",
    detail: "Review your publishing plan when you're ready.",
    primaryLabel: "Review Schedule →",
    primaryRoute: "/publishing/calendar",
    secondaryLabel: "Back to Campaign Room",
    secondaryRoute: "/campaigns",
  },
  more_items_pending: {
    title: "More items are ready.",
    detail: "The department finished additional work while you reviewed.",
    primaryLabel: "Start New Review Session →",
    primaryRoute: "",  // handled by onPrimaryOverride
    secondaryLabel: "Pause for now",
    secondaryRoute: "/home",
  },
  back_to_campaign: {
    title: "Session complete.",
    detail: "Return to the campaign when you're ready.",
    primaryLabel: "Back to Campaign Room →",
    primaryRoute: "/campaigns",
    secondaryLabel: "Back to Executive Home",
    secondaryRoute: "/home",
  },
  no_work_needed: {
    title: "Nothing needs your review yet.",
    detail: "Come back when the department completes the next round.",
    primaryLabel: "Back to Executive Home →",
    primaryRoute: "/home",
    secondaryLabel: "Open Campaign Room",
    secondaryRoute: "/campaigns",
  },
};


export function ReviewCompletionHandoff({
  kind,
  campaignName,
  decisionsMade,
  blueprintId,
  packId,
  onPrimaryOverride,
}: {
  kind: CompletionHandoffKind;
  campaignName: string | null;
  decisionsMade: number;
  blueprintId?: string | null;
  packId?: string | null;
  onPrimaryOverride?: () => void;
}): JSX.Element {
  const nav = useNavigate();
  const c = COPY[kind];

  const primaryRoute =
    kind === "back_to_campaign" && blueprintId
      ? `/campaigns/${blueprintId}/room`
      : kind === "already_scheduling" && blueprintId
      ? "/publishing/calendar"
      : c.primaryRoute;

  return (
    <section
      className="obsidian-card mx-auto max-w-2xl p-8 text-center"
      data-testid="review-completion-handoff"
      data-handoff-kind={kind}
    >
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
        <CheckCircle2 className="h-8 w-8" />
      </div>
      <h1
        className="mt-5 text-2xl font-semibold text-white"
        data-testid="review-completion-title"
      >
        {c.title}
      </h1>
      {campaignName && (
        <p
          className="mt-2 text-sm text-white/60"
          data-testid="review-completion-campaign"
        >
          {campaignName}
        </p>
      )}
      {decisionsMade > 0 && (
        <p
          className="mt-3 text-sm text-white/70"
          data-testid="review-completion-summary"
        >
          {decisionsMade} {decisionsMade === 1 ? "asset" : "assets"} reviewed.
        </p>
      )}
      <p className="mt-4 text-sm text-white/70">{c.detail}</p>
      <div className="mt-6 flex flex-col items-center gap-2">
        <button
          type="button"
          onClick={() => {
            if (onPrimaryOverride) onPrimaryOverride();
            else if (primaryRoute) nav(primaryRoute);
          }}
          className="w-full rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-violet-500 px-6 py-3 text-sm font-semibold text-slate-950 shadow-[0_10px_30px_-10px_rgba(0,240,255,0.5)] hover:brightness-110"
          data-testid="review-completion-primary"
        >
          {c.primaryLabel}
        </button>
        <button
          type="button"
          onClick={() => nav(c.secondaryRoute)}
          className="rounded-full border border-white/10 bg-transparent px-6 py-2 text-sm text-white/70 hover:bg-white/5"
          data-testid="review-completion-secondary"
        >
          {c.secondaryLabel}
        </button>
      </div>
      <div className="sr-only" aria-live="polite">
        Review complete. {campaignName ?? "Campaign"} · {decisionsMade}{" "}
        {decisionsMade === 1 ? "asset" : "assets"} reviewed.
      </div>
      {/* Suppressed void so exhaustive-deps stays happy in future edits. */}
      {packId ? <span data-pack-id={packId} className="hidden" /> : null}
    </section>
  );
}
